import Typography from '@mui/material/Typography';
import Column from 'components/Layout/Column';
import { Control, Controller } from 'react-hook-form';
import { CreateNoticeSchemaFormValues } from '../schema';
import { Button, ButtonBase, FormHelperText, styled } from '@mui/material';
import FormField from 'further-ui/components/FormField';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { useDisclosure } from 'further-ui/hooks';
import Row from 'components/Layout/Row';
import { InfoOutline } from '@mui/icons-material';
import Guidelines from './Guidelines';
import CkEditor from 'components/CkEditor';
import RichText from 'further-ui/components/RichText';

const StyledRichText = styled(RichText)(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.grey[900],
}));

const StyledInfoOutline = styled(InfoOutline)(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize,
}));

const StyledColumn = styled(Column)(() => ({
  maxWidth: '600px',
}));

type Props = {
  readOnly?: boolean;
  control: Control<CreateNoticeSchemaFormValues>;
};

const NoticeNarrative: React.FC<Props> = ({ readOnly, control }) => {
  const descriptionGuidelines = useDisclosure();

  return (
    <StyledColumn spacing="md">
      <FullPageDrawer
        innerScroll
        onClose={descriptionGuidelines.onClose}
        actions={
          <Button
            size="large"
            variant="outlined"
            onClick={descriptionGuidelines.onClose}
          >
            Close
          </Button>
        }
        open={descriptionGuidelines.isOpen}
        title="Capital call description ILPA guidelines"
      >
        <Guidelines />
      </FullPageDrawer>
      <Controller
        control={control}
        name="noticeNarrative.noticeNarrative"
        render={({ field, fieldState }) => (
          <FormField
            label={
              <Row centered spacing="xxs">
                <Typography variant={readOnly ? 'h5' : 'h6'}>
                  Notice narrative
                </Typography>
                {readOnly ? null : (
                  <ButtonBase onClick={descriptionGuidelines.onOpen}>
                    <StyledInfoOutline />
                  </ButtonBase>
                )}
              </Row>
            }
            labelColorLight={readOnly}
          >
            <Column spacing="xs">
              {readOnly ? (
                <Typography variant="h6" color="grey.600">
                  Details
                </Typography>
              ) : null}
              {readOnly ? (
                <StyledRichText openExternalLinksInNewTab text={field.value} />
              ) : (
                <CkEditor
                  addTargetToExternalLinks
                  value={field.value}
                  extraLines={8}
                  onChange={field.onChange}
                  placeholder="Explain why the capital in this notice is being called or distributed. If you wish to follow ILPA standard reporting, please click the tooltip icon in the section title above to view ILPA’s recommended disclosure structure."
                />
              )}
            </Column>
            {fieldState.error ? (
              <FormHelperText error>{fieldState.error.message}</FormHelperText>
            ) : null}
          </FormField>
        )}
      />
    </StyledColumn>
  );
};

export default NoticeNarrative;
